// Amix, el asistente de XmarteK (D52): una onda de voz con cara. Aqui va la cara sin React: los estados, sus guiones de
// gestos, el parpadeo, la respiracion y la geometria de los ojos. Funciones puras, con pruebas; ui/Amix.tsx las dibuja
// cuadro a cuadro.
// Tres reglas: lo que te espera va quieto (solo parpadea), lo que trabaja se mueve en bucle y las emociones de cierre
// tienen un golpe y luego calma.

export type EstadoAmix = 'reposo' | 'saludando' | 'escuchando' | 'pensando' | 'buscando' | 'trabajando' | 'esperando' | 'feliz' | 'celebrando' | 'preocupado' | 'confundido';

// mx, my: mirada (-1..1). salto: px hacia arriba. estira: 1 normal (>1 se alarga). rot: grados. alto/ancho: escala de
// los ojos. alegria: ojos en arco (0..1). asim: un ojo mas grande. incl: cejas preocupadas. ondas: arcos de voz (0..1).
// tiembla: temblor corto.
export type Gesto = { mx: number; my: number; salto: number; estira: number; rot: number; alto: number; ancho: number; alegria: number; asim: number; incl: number; ondas: number; tiembla: number };
export const NEUTRO: Gesto = { mx: 0, my: 0, salto: 0, estira: 1, rot: 0, alto: 1, ancho: 1, alegria: 0, asim: 0, incl: 0, ondas: 0.25, tiembla: 0 };
type Paso = { gesto: Gesto; llega: number; queda: number };
export type Guion = { bucle: boolean; pasos: Paso[] };

const g = (o: Partial<Gesto>): Gesto => ({ ...NEUTRO, ...o });
const paso = (o: Partial<Gesto>, llega: number, queda: number): Paso => ({ gesto: g(o), llega, queda });

export const GUIONES: Record<EstadoAmix, Guion> = {
  reposo: { bucle: true, pasos: [paso({ mx: -0.35, rot: -2 }, 1600, 2400), paso({ mx: 0.35, rot: 2 }, 1800, 2600), paso({ my: 0.05 }, 1400, 1800)] },
  saludando: { bucle: false, pasos: [paso({ alegria: 1, salto: 7, estira: 0.92, rot: -6, ondas: 0.9 }, 260, 60), paso({ alegria: 1, estira: 1.04, rot: 6, ondas: 0.9 }, 280, 80), paso({ alegria: 1, ondas: 0.6 }, 360, 900), paso({}, 500, 0)] },
  escuchando: { bucle: false, pasos: [paso({ my: 0.55, alto: 1.12, ondas: 0.5 }, 420, 0)] },
  pensando: { bucle: true, pasos: [paso({ mx: 0.5, my: -0.5, alto: 0.8, ondas: 0.7, rot: 3 }, 700, 900), paso({ mx: 0.35, my: -0.55, alto: 0.82, ondas: 0.95, rot: 1 }, 900, 700)] },
  buscando: { bucle: true, pasos: [paso({ mx: -0.6, my: 0.1, ondas: 1 }, 360, 160), paso({ mx: 0.6, my: 0.1, ondas: 0.6 }, 420, 160)] },
  trabajando: { bucle: true, pasos: [paso({ my: 0.25, salto: 3, estira: 1.04, alto: 0.6, ancho: 1.1, ondas: 1 }, 220, 0), paso({ my: 0.25, estira: 0.96, alto: 0.6, ancho: 1.1, ondas: 0.7 }, 220, 0)] },
  esperando: { bucle: false, pasos: [paso({ estira: 1.02, alto: 1.18, ancho: 1.06, ondas: 0.35 }, 380, 0)] },
  feliz: { bucle: false, pasos: [paso({ alegria: 1, salto: 6, estira: 0.94, ondas: 0.8 }, 240, 40), paso({ alegria: 1, estira: 1.03, ondas: 0.5 }, 300, 1400), paso({}, 600, 0)] },
  celebrando: { bucle: false, pasos: [paso({ alegria: 1, salto: 12, estira: 0.9, rot: -9, ondas: 1 }, 240, 40), paso({ alegria: 1, estira: 1.06, ondas: 1 }, 220, 40), paso({ alegria: 1, salto: 8, estira: 0.93, rot: 9, ondas: 1 }, 220, 40), paso({ alegria: 1, ondas: 0.6 }, 300, 1800), paso({}, 600, 0)] },
  preocupado: { bucle: false, pasos: [paso({ incl: 1, alto: 0.92, my: 0.15, tiembla: 1, ondas: 0.4 }, 300, 420), paso({ incl: 1, alto: 0.92, my: 0.15, ondas: 0.3 }, 300, 0)] },
  confundido: { bucle: false, pasos: [paso({ rot: -10, asim: 0.35, mx: -0.2, my: -0.2, ondas: 0.2 }, 420, 0)] },
};

// Lo que dice la linea de estado (aria-live) y cuanto dura cada emocion antes de volver al reposo.
export const TEXTO_ESTADO: Record<EstadoAmix, string> = {
  reposo: 'Listo para ayudarte',
  saludando: '¡Hola! Soy Amix',
  escuchando: 'Te escucho…',
  pensando: 'Pensando…',
  buscando: 'Buscando en el CRM…',
  trabajando: 'Escribiendo…',
  esperando: 'Esperando tu respuesta',
  feliz: 'Listo',
  celebrando: '¡Hecho!',
  preocupado: 'Ojo con esto',
  confundido: 'Algo no salió bien',
};
export const DURACION: Partial<Record<EstadoAmix, number>> = { saludando: 2600, feliz: 7000, celebrando: 7000, preocupado: 6000, confundido: 6000 };
// Estados que hacen girar el anillo del boton (trabaja) o encienden el punto ambar (espera).
export const modoLanzador = (e: EstadoAmix) => (e === 'pensando' || e === 'buscando' || e === 'trabajando' ? 'trabaja' : e === 'esperando' ? 'espera' : 'reposo');

export const suave = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

export function mezcla(a: Gesto, b: Gesto, k: number): Gesto {
  const o = { ...a };
  for (const c of Object.keys(NEUTRO) as (keyof Gesto)[]) o[c] = a[c] + (b[c] - a[c]) * k;
  return o;
}

// El gesto a los `ms` de empezar un guion, partiendo de `desde` (el gesto en que estaba al cambiar de estado).
export function gestoEn(guion: Guion, ms: number, desde: Gesto): Gesto {
  const total = guion.pasos.reduce((s, p) => s + p.llega + p.queda, 0);
  let t = ms;
  let previo = desde;
  if (guion.bucle && total > 0 && ms > total) {
    t = ms % total;
    previo = guion.pasos[guion.pasos.length - 1].gesto;
  }
  for (const p of guion.pasos) {
    if (t < p.llega) return mezcla(previo, p.gesto, suave(t / p.llega));
    t -= p.llega;
    if (t < p.queda) return p.gesto;
    t -= p.queda;
    previo = p.gesto;
  }
  return guion.pasos[guion.pasos.length - 1].gesto;
}

// Parpadeo con semilla (0 abierto, 1 cerrado): 220 ms, el primero a 1.5 s y luego cada 2.6 a 5 s. Cada Amix en
// pantalla tiene su semilla, asi no parpadean todos a la vez.
export function parpadeo(ms: number, semilla: number) {
  const DURA = 220;
  let t = 1500;
  let s = semilla >>> 0 || 7;
  while (t <= ms) {
    if (ms < t + DURA) {
      const k = (ms - t) / DURA;
      return k < 0.5 ? k * 2 : (1 - k) * 2;
    }
    s = (Math.imul(s, 1103515245) + 12345) >>> 0;
    t += DURA + 2400 + (s % 2600);
  }
  return 0;
}

export const respiro = (ms: number) => Math.sin((ms / 3400) * Math.PI * 2) * 0.014;

/* ---------------- geometria (lienzo de 100x100) ---------------- */
export const CUERPO = 'M50 22C68 22 79 30 79 50C79 70 69 80 50 80C31 80 21 70 21 50C21 30 32 22 50 22Z';
export const BASE = 80;
const CARA = { x: 50, y: 52 };

export type Ojo = { x: number; y: number; ancho: number; alto: number; abierto: number; arco: string; ceja: string; cejaOp: number; luzX: number; luzY: number };
export type Pose = { todo: string; sombraRx: number; sombraOp: number; ojos: [Ojo, Ojo]; ondas: [number, number, number] };

// La pose completa de un cuadro. `quieto`: sin respiracion, parpadeo ni temblor (reducir movimiento).
export function pose(ge: Gesto, ms: number, semilla: number, quieto = false): Pose {
  const resp = quieto ? 0 : respiro(ms + semilla * 37);
  const cierre = quieto || ge.alegria > 0.5 ? 0 : parpadeo(ms, semilla);
  const estira = ge.estira + resp;
  const sx = 1 / Math.sqrt(estira);
  const tiembla = !quieto && ge.tiembla ? Math.sin(ms / 22) * 1.6 * ge.tiembla : 0;
  const todo = `translate(${r(tiembla)} ${r(-ge.salto)}) rotate(${r(ge.rot)} 50 ${BASE}) translate(50 ${BASE}) scale(${r(sx, 4)} ${r(estira, 4)}) translate(-50 ${-BASE})`;
  const fx = CARA.x + ge.mx * 9;
  const fy = CARA.y + ge.my * 7;
  const ojo = (lado: -1 | 1): Ojo => {
    const sep = 11 * (1 - Math.abs(ge.mx) * 0.18);
    const x = fx + lado * sep;
    const borde = 1 - Math.max(0, lado * ge.mx) * 0.25;
    const asim = lado === 1 ? 1 + ge.asim : 1 - ge.asim * 0.4;
    const ancho = 6.2 * ge.ancho * borde;
    const alto = Math.max(0.6, 11 * ge.alto * asim * (1 - cierre) * (1 - ge.alegria));
    return {
      x: r(x),
      y: r(fy),
      ancho: r(ancho),
      alto: r(alto),
      abierto: r(1 - ge.alegria, 3),
      arco: `M${r(x - 4.4)} ${r(fy + 1.8)}Q${r(x)} ${r(fy - 4.4)} ${r(x + 4.4)} ${r(fy + 1.8)}`,
      ceja: `M${r(x - 4.6)} ${r(fy - 9 - lado * 1.6 * ge.incl)}L${r(x + 4.6)} ${r(fy - 9 + lado * 1.6 * ge.incl)}`,
      cejaOp: r(ge.incl, 3),
      luzX: r(x + ancho * 0.18),
      luzY: r(fy - alto * 0.22),
    };
  };
  const onda = (i: number) => {
    const vaiven = quieto ? 0 : Math.sin(ms / 260 - i) * 0.18 * ge.ondas;
    return r(Math.max(0, Math.min(1, ge.ondas * 1.3 - i * 0.28 + vaiven)), 3);
  };
  return { todo, sombraRx: r(22 - ge.salto * 0.7), sombraOp: r(0.18 - ge.salto * 0.008, 3), ojos: [ojo(-1), ojo(1)], ondas: [onda(0), onda(1), onda(2)] };
}

function r(n: number, d = 2) {
  const f = 10 ** d;
  return Math.round(n * f) / f;
}

// La emocion con que el asistente cierra su respuesta: "[[estado:feliz]]" al principio o al final del texto.
const ETIQUETA = /\[\[estado:(feliz|celebrando|preocupado|confundido)\]\]/gi;
export function separarEstado(texto: string): { texto: string; estado: EstadoAmix | null } {
  let estado: EstadoAmix | null = null;
  const limpio = texto.replace(ETIQUETA, (_, e: string) => {
    estado = e.toLowerCase() as EstadoAmix;
    return '';
  });
  return { texto: estado ? limpio.replace(/^\s+/, '').replace(/\s+$/, '') : texto, estado };
}
